import { useState, useEffect } from 'react';
import {
    Header,
    Table,
    Button,
    Form,
    FormField,
    Input,
    Checkbox,
    Message,
    Icon,
    Label,
} from 'semantic-ui-react';
import { LoadingWrapper } from '../../common';
import {
    getRoadblockTypes,
    getAllRoadblockTypes,
    createRoadblockType,
    updateRoadblockType,
    createRoadblockSubtype,
    updateRoadblockSubtype,
    FieldValidationError,
} from './roadblockService';
import { isICCSupport, canEditAll, isReadOnly } from '../../auth';

const DEFAULT_TYPE_FORM = { label: '', requiresDescription: false };

// ─── SubtypeRow ───────────────────────────────────────────────────────────────

const SubtypeRow = ({ subtype, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [label, setLabel]     = useState(subtype.label);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState('');

    const cancelEdit = () => { setEditing(false); setLabel(subtype.label); setError(''); };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const updated = await updateRoadblockSubtype(subtype.id, { label: label.trim() });
            onSaved(updated);
            setEditing(false);
        } catch (err) {
            setError(err instanceof FieldValidationError ? err.fields.label || err.message : err.message);
        } finally { setSaving(false); }
    };

    const toggleActive = async () => {
        try {
            const updated = await updateRoadblockSubtype(subtype.id, { isActive: !subtype.isActive });
            onSaved(updated);
        } catch (err) { setError(err.message); }
    };

    return (
        <Table.Row style={{ opacity: subtype.isActive ? 1 : 0.5, background: '#f5f7ff', borderLeft: '3px solid #c5cae9' }}>
            <Table.Cell style={{ paddingLeft: '2.5rem' }}>
                {editing ? (
                    <>
                        <Input size="mini" value={label} onChange={(e, { value }) => setLabel(value)} error={!!error} style={{ minWidth: '12rem' }} />
                        {error && <div style={{ color: '#9f3a38', fontSize: '0.8em' }}>{error}</div>}
                    </>
                ) : subtype.label}
            </Table.Cell>
            <Table.Cell>—</Table.Cell>
            <Table.Cell>—</Table.Cell>
            <Table.Cell>
                <Label size="mini" color={subtype.isActive ? 'green' : 'grey'} content={subtype.isActive ? 'Active' : 'Inactive'} />
            </Table.Cell>
            <Table.Cell>
                {editing ? (
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <Button size="mini" primary onClick={save} loading={saving} disabled={saving}>Save</Button>
                        <Button size="mini" onClick={cancelEdit}>Cancel</Button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                        <Button size="mini" basic icon labelPosition="left" onClick={() => setEditing(true)}>
                            <Icon name="edit" /> Edit
                        </Button>
                        <Button size="mini" basic color={subtype.isActive ? 'orange' : 'green'} icon labelPosition="left" onClick={toggleActive}>
                            <Icon name={subtype.isActive ? 'ban' : 'check circle'} />
                            {subtype.isActive ? 'Deactivate' : 'Reactivate'}
                        </Button>

                    </div>
                )}
            </Table.Cell>
        </Table.Row>
    );
};

// ─── TypeRow ──────────────────────────────────────────────────────────────────

const TypeRow = ({ type, onTypeUpdated }) => {
    const initForm = { label: type.label, requiresDescription: type.requiresDescription };

    const [editing, setEditing]       = useState(false);
    const [editForm, setEditForm]     = useState(initForm);
    const [editErrors, setEditErrors] = useState({});
    const [saving, setSaving]         = useState(false);
    const [error, setError]           = useState('');
    const [expanded, setExpanded]     = useState(false);
    const [addSubLabel, setAddSubLabel] = useState('');
    const [addingSub, setAddingSub]   = useState(false);
    const [subtypeError, setSubtypeError] = useState('');

    const cancelEdit = () => { setEditing(false); setEditErrors({}); setEditForm(initForm); };

    const saveType = async () => {
        if (!editForm.label.trim()) { setEditErrors({ label: 'Label is required.' }); return; }
        setSaving(true);
        setError('');
        try {
            const updated = await updateRoadblockType(type.id, {
                label: editForm.label.trim(),
                requiresDescription: editForm.requiresDescription,
            });
            onTypeUpdated(updated);
            setEditing(false);
        } catch (err) {
            if (err instanceof FieldValidationError) setEditErrors(err.fields);
            else setError(err.message);
        } finally { setSaving(false); }
    };

    const toggleActive = async () => {
        setError('');
        try {
            const updated = await updateRoadblockType(type.id, { isActive: !type.isActive });
            onTypeUpdated(updated);
        } catch (err) { setError(err.message); }
    };

    const handleSubtypeSaved = (updated) =>
        onTypeUpdated({ ...type, subtypes: type.subtypes.map(s => s.id === updated.id ? updated : s) });

    const addSubtype = async () => {
        if (!addSubLabel.trim()) { setSubtypeError('Label is required.'); return; }
        setAddingSub(true);
        setSubtypeError('');
        try {
            const created = await createRoadblockSubtype(type.id, { label: addSubLabel.trim(), parent: type.id });
            onTypeUpdated({ ...type, subtypes: [...(type.subtypes || []), created] });
            setAddSubLabel('');
        } catch (err) {
            if (err instanceof FieldValidationError) setSubtypeError(err.fields.label || err.message);
            else setSubtypeError(err.message);
        } finally { setAddingSub(false); }
    };

    return (
        <>
            <Table.Row style={{ opacity: type.isActive ? 1 : 0.55 }}>
                <Table.Cell>
                    {editing ? (
                        <>
                            <Input size="small" value={editForm.label} onChange={(e, { value }) => setEditForm(p => ({ ...p, label: value }))} error={!!editErrors.label} style={{ minWidth: '14rem' }} />
                            {editErrors.label && <div style={{ color: '#9f3a38', fontSize: '0.8em' }}>{editErrors.label}</div>}
                        </>
                    ) : type.label}
                    {error && <div style={{ color: '#9f3a38', fontSize: '0.8em', marginTop: '0.2em' }}>{error}</div>}
                </Table.Cell>
                <Table.Cell textAlign="center">
                    {editing
                        ? <Checkbox toggle checked={editForm.requiresDescription} onChange={(e, { checked }) => setEditForm(p => ({ ...p, requiresDescription: checked }))} />
                        : type.requiresDescription ? <Icon name="check" color="green" /> : <Icon name="minus" color="grey" />
                    }
                </Table.Cell>
                <Table.Cell>
                    <Button size="mini" basic onClick={() => setExpanded(e => !e)}>
                        <Icon name={expanded ? 'chevron up' : 'chevron down'} />
                        {type.subtypes?.length ?? 0} subtypes
                    </Button>
                </Table.Cell>
                <Table.Cell>
                    <Label size="small" color={type.isActive ? 'green' : 'grey'} content={type.isActive ? 'Active' : 'Inactive'} />
                </Table.Cell>
                <Table.Cell>
                    {editing ? (
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <Button size="mini" primary onClick={saveType} loading={saving} disabled={saving}>Save</Button>
                            <Button size="mini" onClick={cancelEdit}>Cancel</Button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                            <Button size="mini" basic icon labelPosition="left" onClick={() => setEditing(true)}>
                                <Icon name="edit" /> Edit
                            </Button>
                            <Button size="mini" basic color={type.isActive ? 'orange' : 'green'} icon labelPosition="left" onClick={toggleActive}>
                                <Icon name={type.isActive ? 'ban' : 'check circle'} />
                                {type.isActive ? 'Deactivate' : 'Reactivate'}
                            </Button>
                        </div>
                    )}
                </Table.Cell>
            </Table.Row>

            {expanded && (
                <>
                    {(type.subtypes || []).map(st => (
                        <SubtypeRow key={st.id} subtype={st} onSaved={handleSubtypeSaved} />
                    ))}
                    <Table.Row style={{ background: '#f0f4ff' }}>
                        <Table.Cell colSpan={5} style={{ paddingLeft: '2.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Input size="mini" placeholder="New subtype label..." value={addSubLabel}
                                    onChange={(e, { value }) => { setAddSubLabel(value); setSubtypeError(''); }}
                                    error={!!subtypeError} style={{ minWidth: '16rem' }}
                                />
                                <Button size="mini" primary onClick={addSubtype} loading={addingSub} disabled={addingSub || !addSubLabel.trim()}>
                                    <Icon name="plus" /> Add Subtype
                                </Button>
                                {subtypeError && <span style={{ color: '#9f3a38', fontSize: '0.85em' }}>{subtypeError}</span>}
                            </div>
                        </Table.Cell>
                    </Table.Row>
                </>
            )}
        </>
    );
};

// ─── RoadblockTypeManager ─────────────────────────────────────────────────────

export const RoadblockTypeManager = () => {
    const [types, setTypes]       = useState([]);
    const [loaded, setLoaded]     = useState(false);
    const [error, setError]       = useState('');
    const [addForm, setAddForm]   = useState(DEFAULT_TYPE_FORM);
    const [addErrors, setAddErrors] = useState({});
    const [adding, setAdding]     = useState(false);

    const canManage = !isReadOnly() && (isICCSupport() || canEditAll());

    useEffect(() => {
        if (!canManage) { setLoaded(true); return; }
        getAllRoadblockTypes()
            .then(data => setTypes(data))
            .catch(err => setError(err.message))
            .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!canManage) {
        return (
            <Message negative>
                <Message.Header>Access Denied</Message.Header>
                <p>Only ICC Support and superusers can manage roadblock types.</p>
            </Message>
        );
    }

    const handleTypeUpdated = (updated) =>
        setTypes(prev => prev.map(t => t.id === updated.id ? updated : t));

    const handleAdd = async () => {
        const errors = {};
        if (!addForm.label.trim()) errors.label = 'Label is required.';
        if (Object.keys(errors).length) { setAddErrors(errors); return; }

        setAdding(true);
        setError('');
        try {
            const created = await createRoadblockType({
                label: addForm.label.trim(),
                requiresDescription: addForm.requiresDescription,
            });
            setTypes(prev => [...prev, { ...created, subtypes: [] }]);
            setAddForm(DEFAULT_TYPE_FORM);
            setAddErrors({});
        } catch (err) {
            if (err instanceof FieldValidationError) setAddErrors(err.fields);
            else setError(err.message);
        } finally { setAdding(false); }
    };

    const activeTypes   = types.filter(t => t.isActive);
    const inactiveTypes = types.filter(t => !t.isActive);

    return (
        <LoadingWrapper loading={!loaded}>
            <div style={{ padding: '1.5rem', maxWidth: '960px', margin: '0 auto' }}>
                <Header as="h2">Roadblock Type Manager</Header>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Manage roadblock types and their subtypes. Deactivated types/subtypes are hidden
                    from forms but existing roadblocks retain their type. Expand a row to manage subtypes.
                </p>

                {error && <Message negative onDismiss={() => setError('')}>{error}</Message>}

                <Table celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Label</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">Req. Description</Table.HeaderCell>
                            <Table.HeaderCell>Subtypes</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {activeTypes.map(t => (
                            <TypeRow key={t.id} type={t} onTypeUpdated={handleTypeUpdated} />
                        ))}
                        {inactiveTypes.length > 0 && activeTypes.length > 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={5} style={{ background: '#f5f5f5', color: '#888', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Inactive types
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {inactiveTypes.map(t => (
                            <TypeRow key={t.id} type={t} onTypeUpdated={handleTypeUpdated} />
                        ))}
                        {types.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={5} textAlign="center" style={{ color: '#999' }}>
                                    No roadblock types found.
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>

                <Header as="h4" style={{ marginTop: '2rem', textAlign: 'center' }}>Add New Type</Header>
                <Form style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <FormField required error={!!addErrors.label}>
                        <label>Label</label>
                        <Input
                            placeholder="e.g. Permits, Safety Issue"
                            value={addForm.label}
                            onChange={(e, { value }) => {
                                setAddForm(prev => ({ ...prev, label: value }));
                                if (addErrors.label) setAddErrors(prev => ({ ...prev, label: null }));
                            }}
                        />
                        {addErrors.label && (
                            <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.2em' }}>{addErrors.label}</div>
                        )}
                    </FormField>

                    <FormField>
                        <Checkbox
                            label="Requires free-text description when selected"
                            checked={addForm.requiresDescription}
                            onChange={(e, { checked }) => setAddForm(prev => ({ ...prev, requiresDescription: checked }))}
                        />
                    </FormField>

                    <Button primary onClick={handleAdd} loading={adding} disabled={adding || !addForm.label.trim()}>
                        <Icon name="plus" /> Add Type
                    </Button>
                </Form>
            </div>
        </LoadingWrapper>
    );
};
